import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, Calendar, Building2, Users } from "lucide-react";
import { motion, useInView, useAnimation, AnimatePresence } from "framer-motion";
import { ScrollReveal } from "@/components/ScrollReveal";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/5xd6forl_4ee9e59f-9646-47a7-8cec-3425f97c54ff.png";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/rv2ioisg_cleaners.jpeg";
const PROJECT_IMG_1 = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/4zuiwd6e_ROSELLA%20APARTMENTS.jpg";
const PROJECT_IMG_2 = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/r0ho9ps7_Monash%20Uni.jpg";
const PROJECT_IMG_3 = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/gc22lsmj_65efa80f50b6ef0921ae1c12_62687fc159ef4b6633ce2c7b_William_Angliss-47_WEB.jpeg";
const PROJECT_IMG_4 = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/b36dgykf_castlerock%20nepean%20hwy.webp";
const PROJECT_IMG_5 = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/r0ho9ps7_Monash%20Uni.jpg";
const PROJECT_IMG_6 = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/d6z8zxzr_WestUrban.jpg";

const ROTATING_LINES = [
  "Builder's Cleaning",
  "Industrial & Plant Cleaning",
  "Office Cleaning Services",
  "Window & Façade Cleaning",
  "Strata & Facilities Cleaning",
  "Pressure Washing",
];

/* Partners with actual logos */
const PARTNERS = [
  { name: "Markscon", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/vg542kmo_Layer-1-1-re8kajg0l24lnwr1bxl2qm0puyxymjoszljw0q5h4w.png" },
  { name: "Intermain", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/d5bfu5iq_Intermain%20Logo1.jpeg" },
  { name: "Jellis Craig", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/jebj2wp5_JellisCraig%20Logo.webp" },
  { name: "Renascent", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/1dgjtg7q_Renascent%20Logo.gif" },
  { name: "Castlerock", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/wp1puvix_Castlerock%20Logo.svg" },
  { name: "Westurban", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/f87ewn43_OIP.webp" },
  { name: "Johns Lyng Group", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/fw0n2f5x_Johns-Lyng-Group_Logo-Light.webp" },
  { name: "Excel Strata Managers", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/eg6ujqe1_ExcelStrata.webp" },
  { name: "TM Strata", logo: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/rb7bjbfa_logo-white.webp" },
];

/* Featured Projects Data - 4 projects shown on landing page */
const FEATURED_PROJECTS = [
  { 
    id: 1,
    title: "Rosella Apartments", 
    type: "Builder's Cleaning", 
    value: "Multi-Storey Residential", 
    client: "Markscon", 
    builder: "Markscon",
    completionDate: "2025",
    image: PROJECT_IMG_1,
    description: "A comprehensive builder's cleaning for this stunning multi-storey residential development. Our team delivered meticulous cleaning services, ensuring every apartment was handover-ready.",
    scope: [
      "Full internal cleaning of all residential units",
      "Common area and lobby detailing",
      "Window cleaning - internal and external",
      "Balcony and facade cleaning",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Multi-storey residential complex",
    projectDetails: {
      location: "Melbourne, VIC",
      duration: "4 weeks",
      teamSize: "12 cleaners",
      sqMetres: "8,500 m²"
    },
    outcome: "Delivered on time with zero defects reported at handover. All 48 apartments passed first inspection, enabling the developer to meet settlement deadlines. Client reported a 100% satisfaction rate from new residents."
  },
  { 
    id: 2,
    title: "Monash University Clayton Campus", 
    type: "Deep Cleaning & Sanitization", 
    value: "Educational Institution", 
    client: "Intermain", 
    builder: "Intermain",
    completionDate: "2026",
    image: PROJECT_IMG_2,
    description: "A comprehensive deep cleaning and sanitization project for Monash University's Clayton Campus rest room facilities. Our team delivered thorough cleaning and sanitization services across multiple buildings, ensuring the highest hygiene standards for students and staff.",
    scope: [
      "Deep cleaning of all rest room facilities",
      "Full sanitization and disinfection",
      "Tile and grout cleaning",
      "Fixture polishing and detailing",
      "Air freshening and odour treatment",
      "Final inspection and quality assurance"
    ],
    size: "University campus facilities",
    projectDetails: {
      location: "Clayton, VIC",
      duration: "2 weeks",
      teamSize: "14 cleaners",
      sqMetres: "3,200 m²"
    },
    outcome: "Restored facilities to as-new condition with measurable improvement in hygiene audit scores. University facilities management reported a 40% reduction in maintenance call-outs following our deep clean program."
  },
  { 
    id: 3,
    title: "William Angliss Institute", 
    type: "Fit Out & Commercial Kitchen Cleaning", 
    value: "Educational Facility", 
    client: "Renascent", 
    builder: "Renascent",
    completionDate: "2026",
    image: PROJECT_IMG_3,
    description: "A detailed fitout cleaning for the William Angliss Institute's modern training facilities. Our team delivered meticulous cleaning of specialist kitchen, hospitality and training spaces, ensuring all areas were ready for student use.",
    scope: [
      "Commercial kitchen deep cleaning",
      "Training facility cleaning",
      "Common area and lobby detailing",
      "Window cleaning - internal and external",
      "Floor treatment and polishing",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Educational training facility",
    projectDetails: {
      location: "Melbourne CBD, VIC",
      duration: "2 weeks",
      teamSize: "10 cleaners",
      sqMetres: "4,800 m²"
    },
    outcome: "All commercial kitchens passed health and safety inspections on first assessment. Specialist food-grade cleaning protocols ensured compliance with hospitality industry standards. Facility opened on schedule for the new academic term."
  },
  { 
    id: 4,
    title: "56 Kingloch Parade", 
    type: "Builder's Cleaning & Handover", 
    value: "Medium Density Townhouse & Apartment Development", 
    client: "WestUrban", 
    builder: "WestUrban",
    completionDate: "2025",
    image: PROJECT_IMG_6,
    description: "A comprehensive builder's cleaning and handover for this medium density townhouse and apartment development in Wantirna. Our team delivered meticulous cleaning across multiple dwellings, ensuring each property was ready for settlement.",
    scope: [
      "Full internal cleaning of all townhouses and apartments",
      "Common area and driveway cleaning",
      "Window cleaning - internal and external",
      "Garage and carport detailing",
      "External facade and balcony cleaning",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Medium density residential development",
    projectDetails: {
      location: "Wantirna, VIC",
      duration: "5 weeks",
      teamSize: "15 cleaners",
      sqMetres: "12,000 m²"
    },
    outcome: "All 24 dwellings achieved settlement-ready status on schedule. Coordinated cleaning across staged handovers, enabling the developer to process settlements efficiently. Zero post-settlement cleaning complaints received."
  },
];

/* Case Study Modal Component */
const CaseStudyModal = ({ project, onClose, onNextProject }) => {
  if (!project) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      data-testid="case-study-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3 }}
        className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className="relative aspect-video">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 transition-colors duration-300"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 md:p-8">
            <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.15em] uppercase mb-2">
              {project.type}
            </p>
            <h2 className="text-white text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {project.title}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Project Details Grid */}
          {project.projectDetails && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 pb-8 border-b border-neutral-200">
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Location</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.location}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Duration</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.duration}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Team Size</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.teamSize}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Area</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.sqMetres}</p>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Project Overview
            </h3>
            <p className="text-neutral-600 leading-relaxed text-sm">{project.description}</p>
          </div>

          {/* Scope of Work */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Scope of Work
            </h3>
            <ul className="space-y-2">
              {project.scope.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-[#B8D86B] rounded-full mt-1.5 shrink-0"></span>
                  <span className="text-neutral-600 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Outcome Section */}
          {project.outcome && (
            <div className="mb-8 p-4 bg-neutral-50 border-l-4 border-[#B8D86B]">
              <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-2">Outcome</h3>
              <p className="text-neutral-600 leading-relaxed text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {project.outcome}
              </p>
            </div>
          )}

          {/* Next Project Button */}
          <div className="pt-6 border-t border-neutral-200">
            <button
              onClick={onNextProject}
              className="inline-flex items-center gap-2 bg-[#B8D86B] text-[#1A2E05] px-8 py-3 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
            >
              View Next Case Study
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const IconLayers = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24 6L4 18l20 12 20-12L24 6z" />
    <path d="M4 26l20 12 20-12" />
    <path d="M4 34l20 12 20-12" />
  </svg>
);

const IconFactory = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 42V18l12-8v10l12-8v10l12-8v18H4z" />
    <rect x="12" y="30" width="6" height="6" />
    <rect x="26" y="30" width="6" height="6" />
  </svg>
);

const IconDiamond = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24 4L4 24l20 20 20-20L24 4z" />
    <path d="M12 16l12 12 12-12" />
  </svg>
);

const IconShield = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24 4L6 12v12c0 11 8 18 18 22 10-4 18-11 18-22V12L24 4z" />
    <path d="M16 24l6 6 10-12" />
  </svg>
);

const IconTool = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M28 8a8 8 0 00-11.3 11.3l-12 12a4 4 0 005.6 5.6l12-12A8 8 0 0028 8z" />
    <path d="M20 16l-4-4" />
    <circle cx="36" cy="36" r="6" />
    <path d="M32 32l8 8" />
  </svg>
);

const IconSparkle = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24 4v8M24 36v8M4 24h8M36 24h8M10 10l6 6M32 32l6 6M10 38l6-6M32 16l6-6" />
    <circle cx="24" cy="24" r="6" />
  </svg>
);

/* Letter-by-letter animated text like COI Capital */
const LetterReveal = ({ text, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <span ref={ref} className={className}>
      <span className="sr-only">{text}</span>
      <motion.span
        aria-hidden
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={{
          visible: {
            transition: { staggerChildren: 0.04, delayChildren: delay },
          },
          hidden: {},
        }}
      >
        {text.split("").map((char, i) => (
          <motion.span
            key={`${char}-${i}`}
            className="inline-block"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 },
            }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {char === " " ? "\u00A0" : char}
          </motion.span>
        ))}
      </motion.span>
    </span>
  );
};

/* Cycling text with letter-by-letter reveal */
const CyclingLetterReveal = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % ROTATING_LINES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []); // ROTATING_LINES is a constant, no need to include

  return (
    <span className="inline-block relative h-[1.15em] overflow-hidden align-bottom">
      <AnimatePresence mode="wait">
        <motion.span
          key={ROTATING_LINES[index]}
          className="inline-block text-[#B8D86B]"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.03, delayChildren: 0.1 },
            },
            exit: {
              transition: { staggerChildren: 0.02, staggerDirection: -1 },
            },
          }}
        >
          {ROTATING_LINES[index].split("").map((char, i) => (
            <motion.span
              key={`${char}-${i}`}
              className="inline-block"
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: { opacity: 1, y: 0 },
                exit: { opacity: 0, y: -40 },
              }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

/* About Section Image Carousel */
const CAROUSEL_IMAGES = [
  { src: "https://images.unsplash.com/photo-1768270181430-3e3672a32283?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBidWlsZGluZyUyMGludGVyaW9yJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MzkwMTE0NHww&ixlib=rb-4.1.0&q=85", alt: "Modern lobby with marble floors" },
  { src: "https://images.unsplash.com/photo-1758448721162-0c77cf477d6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBsb2JieSUyMGludGVyaW9yJTIwY2xlYW4lMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczOTAxMTQzfDA&ixlib=rb-4.1.0&q=85", alt: "Modern reception desk" },
  { src: "https://images.unsplash.com/photo-1758193017781-e3aee6c3e359?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBsb2JieSUyMGludGVyaW9yJTIwY2xlYW4lMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczOTAxMTQzfDA&ixlib=rb-4.1.0&q=85", alt: "Modern elevator lobby" },
  { src: "https://images.unsplash.com/photo-1772112334845-86016056137b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMG1vZGVybiUyMGxpdmluZyUyMHNwYWNlJTIwcHJpc3RpbmV8ZW58MHx8fHwxNzczOTAxMTUxfDA&ixlib=rb-4.1.0&q=85", alt: "Modern living room" },
  { src: "https://images.unsplash.com/photo-1676213185722-252caa34d1ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMG1vZGVybiUyMGxpdmluZyUyMHNwYWNlJTIwcHJpc3RpbmV8ZW58MHx8fHwxNzczOTAxMTUxfDA&ixlib=rb-4.1.0&q=85", alt: "Luxury apartment interior" },
];

const ImageCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []); // CAROUSEL_IMAGES is a constant, no need to include

  return (
    <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-sm">
      <AnimatePresence mode="wait">
        <motion.img
          key={currentIndex}
          src={CAROUSEL_IMAGES[currentIndex].src}
          alt={CAROUSEL_IMAGES[currentIndex].alt}
          className="w-full h-full object-cover absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        />
      </AnimatePresence>
      {/* Progress dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {CAROUSEL_IMAGES.map((img, idx) => (
          <button
            key={img}
            onClick={() => setCurrentIndex(idx)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              idx === currentIndex ? "bg-[#B8D86B] w-6" : "bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

/* Services data with colors and images */
const SERVICES_DATA = [
  { title: "Builder's Cleaning", subtitle: "Construction & Handover", desc: "Safe Work Australia compliant cleaning from rough stages to final sparkle. Fully insured with $20M public liability. SWMS documentation provided for all Tier 1, 2 & 3 builder sites.", color: null, image: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/e1jni46i_11.jpg" },
  { title: "Industrial Cleaning", subtitle: "Warehouses & Factories", desc: "WHS compliant industrial cleaning with confined space and working at heights certifications. EPA-approved waste disposal. Servicing warehouses, manufacturing plants and distribution centres.", color: null, image: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/cknabjp4_Untitled%20%281200%20x%20900%20px%29.png" },
  { title: "Facilities Management", subtitle: "Apartments & Complexes", desc: "Trusted by body corporates and owners corporations across Melbourne. SCA (Strata Community Association) standards. Scheduled cleaning for common areas, car parks, pools and amenities.", color: null, image: "https://images.unsplash.com/photo-1758193017781-e3aee6c3e359?crop=entropy&cs=srgb&fm=jpg&w=800&q=85" },
  { title: "Office Cleaning", subtitle: "Commercial Workspaces", desc: "GECA-certified cleaning products available. Daily, weekly or one-off commercial cleans. Servicing offices, medical centres, retail and government facilities across Melbourne.", color: null, image: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/efcgibnr_office.jpg" },
  { title: "Deep Cleaning", subtitle: "Intensive Services", desc: "Hospital-grade sanitisation using TGA-approved disinfectants. Comprehensive deep cleaning for end-of-lease, pre-sale preparation, post-construction and periodic maintenance.", color: null, image: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/syct2a09_503f4b83a8c27ccd9c5f9f17542b912e.jpg" },
  { title: "Window & Facade Cleaning", subtitle: "IRATA Certified", desc: "IRATA rope access certified. EWP licensed operators. Specialist facade, cladding and external glass cleaning for high-rise and difficult-to-access buildings. Full insurance coverage.", color: null, image: "https://images.unsplash.com/photo-1761689502577-0013be84f1bf?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { title: "Pressure Washing", subtitle: "Commercial Grade", desc: "EPA-compliant water management. Commercial-grade pressure washing for driveways, car parks, pathways and building exteriors. Graffiti removal and surface restoration services available.", color: null, image: "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/qoxikyon_1ee8394274a8f8e7fcf3c0fa7820a3fe.jpg" },
];

/* Flip Card Component with scroll trigger */
const FlipCard = ({ service, index }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: false, amount: 0.6 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []); // Only run on mount

  // Auto-flip on scroll for mobile
  useEffect(() => {
    if (isMobile && isInView) {
      const timer = setTimeout(() => setIsFlipped(true), 300);
      const resetTimer = setTimeout(() => setIsFlipped(false), 3000);
      return () => {
        clearTimeout(timer);
        clearTimeout(resetTimer);
      };
    }
  }, [isMobile, isInView]);

  return (
    <div 
      ref={cardRef}
      className="perspective-1000 h-64 sm:h-72 md:h-80 cursor-pointer relative"
      onMouseEnter={() => !isMobile && setIsFlipped(true)}
      onMouseLeave={() => !isMobile && setIsFlipped(false)}
      onClick={() => isMobile && setIsFlipped(!isFlipped)}
      data-testid={`service-card-${index}`}
      style={{ zIndex: isFlipped ? 10 : 1 }}
    >
      <motion.div
        className="relative w-full h-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Front */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ backgroundColor: service.color || '#1A1A1A', backfaceVisibility: "hidden" }}
        >
          {service.image && (
            <>
              <img 
                src={service.image} 
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40" />
            </>
          )}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8">
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-white tracking-tight uppercase leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {service.title}
            </h3>
            <p className="text-white/70 text-xs sm:text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {service.subtitle}
            </p>
          </div>
        </div>
        
        {/* Back */}
        <div 
          className="absolute inset-0 flex flex-col justify-between p-4 sm:p-5 md:p-8 overflow-hidden"
          style={{ backgroundColor: "#B8D86B", backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
        >
          <div className="overflow-y-auto flex-1">
            <h4 className="text-[#111] font-semibold text-sm mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {service.title}
            </h4>
            <p className="text-[#111]/80 text-[11px] sm:text-xs md:text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {service.desc}
            </p>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-[#111] hover:text-[#111]/70 transition-colors mt-3 shrink-0"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Find out more
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

/* Services Section with Flip Cards */
const ServicesSection = () => {
  return (
    <section data-testid="services-preview" className="py-24 md:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ScrollReveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div>
              <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.25em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Our Services
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                Services<span className="text-[#B8D86B]">.</span>
              </h2>
            </div>
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 border-b-2 border-neutral-900 pb-1 hover:border-[#B8D86B] hover:text-[#B8D86B] transition-colors duration-300 w-fit"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </ScrollReveal>

        {/* Flip Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {SERVICES_DATA.map((service, idx) => (
            <ScrollReveal key={service.title} delay={idx * 0.05}>
              <FlipCard service={service} index={idx} />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function LandingPage() {
  const [selectedProject, setSelectedProject] = useState(null);

  const handleNextProject = () => {
    if (!selectedProject) return;
    const currentIndex = FEATURED_PROJECTS.findIndex(p => p.id === selectedProject.id);
    const nextIndex = (currentIndex + 1) % FEATURED_PROJECTS.length;
    setSelectedProject(FEATURED_PROJECTS[nextIndex]);
  };
  
  return (
    <div data-testid="landing-page" className="bg-white">
      {/* Hero - Same size as other pages */}
      <section data-testid="hero-section" className="pt-32 pb-20 md:pt-44 md:pb-28 relative overflow-hidden min-h-[60vh] md:min-h-[55vh]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Melbourne CBD Skyline" className="w-full h-full object-cover object-[70%_center] md:object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/50" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <p className="text-[#B8D86B] text-xs md:text-sm font-semibold tracking-[0.2em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              PKC Property Services
            </p>
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight max-w-4xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Specialists in{' '}
              <CyclingLetterReveal />
              <span className="text-[#B8D86B]">.</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              High-standard commercial cleaning across Melbourne's construction, commercial and industrial sectors.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link
                to="/contact"
                data-testid="hero-cta-btn"
                className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-8 py-4 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Request a Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/ourwork"
                data-testid="hero-projects-btn"
                className="inline-flex items-center gap-2 text-white border border-white/30 px-8 py-4 text-sm font-semibold hover:bg-white/10 transition-colors duration-300"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                View Our Work
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* About */}
      <section data-testid="about-preview" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16">
            <div className="lg:col-span-5">
              <ScrollReveal>
                <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.25em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  About PKC
                </p>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 leading-[0.95]" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                  A Higher
                  <br />
                  <span className="text-[#B8D86B]">Standard of</span>
                  <br />
                  Quality.
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={0.2}>
                <div className="mt-10">
                  <Link
                    to="/about"
                    data-testid="about-learn-more-btn"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 border-b-2 border-neutral-900 pb-1 hover:border-[#B8D86B] hover:text-[#B8D86B] transition-colors duration-300"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    About Us
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-7">
              <ScrollReveal delay={0.1}>
                <p className="text-neutral-700 text-lg md:text-xl leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  PKC Property Services delivers high-standard commercial cleaning across Melbourne's construction, commercial and industrial sectors. From builder's cleaning through to ongoing maintenance, we work alongside construction teams, property managers and strata to ensure every site is completed to a consistent, handover-ready standard.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={0.15}>
                <p className="text-neutral-500 text-base md:text-lg leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Our services scale across projects of all sizes — from office fitouts to large-scale developments and complex facilities. Each engagement is tailored to meet the specific requirements, timelines and operational demands of your site, with <span className="text-neutral-700 font-medium">specialist capability in high-rise window and façade cleaning</span>.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={0.2}>
                <p className="text-neutral-500 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Safety underpins every project we deliver. Our operations are fully aligned with <span className="text-neutral-700 font-medium">WHS requirements</span>, supported by comprehensive SWMS documentation, regular site briefings, and ongoing staff training. We maintain all required insurances and certifications, ensuring seamless integration with your site teams and zero disruption to project delivery.
                </p>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <ServicesSection />

      {/* Our Work */}
      <section data-testid="featured-projects" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-12 md:mb-16">
              <div>
                <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.25em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Recent Work
                </p>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                  Our Work<span className="text-[#B8D86B]">.</span>
                </h2>
              </div>
              <Link
                to="/ourwork"
                data-testid="view-all-projects-btn"
                className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 border-b-2 border-neutral-900 pb-1 hover:border-[#B8D86B] hover:text-[#B8D86B] transition-colors duration-300"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ScrollReveal>
              <div 
                className="group relative aspect-[4/3] overflow-hidden" 
                data-testid="project-card-0"
              >
                <img src={PROJECT_IMG_1} alt="Rosella Apartments" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <p className="text-[#B8D86B] text-sm font-bold tracking-wide uppercase mb-2">Builder's Cleaning</p>
                  <h3 className="text-white text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Rosella Apartments</h3>
                  <p className="text-white text-sm font-bold mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Markscon</p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div 
                className="group relative aspect-[4/3] overflow-hidden" 
                data-testid="project-card-1"
              >
                <img src={PROJECT_IMG_2} alt="Monash University Clayton Campus" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <p className="text-[#B8D86B] text-sm font-bold tracking-wide uppercase mb-2">Deep Cleaning & Sanitization</p>
                  <h3 className="text-white text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Monash University Clayton Campus</h3>
                  <p className="text-white text-sm font-bold mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Intermain</p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div 
                className="group relative aspect-[4/3] overflow-hidden" 
                data-testid="project-card-2"
              >
                <img src={PROJECT_IMG_3} alt="William Angliss Institute" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <p className="text-[#B8D86B] text-sm font-bold tracking-wide uppercase mb-2">Fit Out & Commercial Kitchen Cleaning</p>
                  <h3 className="text-white text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>William Angliss Institute</h3>
                  <p className="text-white text-sm font-bold mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Renascent</p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div 
                className="group relative aspect-[4/3] overflow-hidden" 
                data-testid="project-card-3"
              >
                <img src={PROJECT_IMG_6} alt="56 Kingloch Parade" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <p className="text-[#B8D86B] text-sm font-bold tracking-wide uppercase mb-2">Builder's Cleaning & Handover</p>
                  <h3 className="text-white text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>56 Kingloch Parade</h3>
                  <p className="text-white text-sm font-bold mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>WestUrban</p>
                </div>
              </div>
            </ScrollReveal>
          </div>

          <div className="md:hidden mt-8">
            <Link to="/ourwork" className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 border-b-2 border-neutral-900 pb-1 hover:text-[#B8D86B] hover:border-[#B8D86B] transition-colors">
              View All Work <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section data-testid="partners-section" className="py-12 md:py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <ScrollReveal>
            <h2 className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Trusted By
            </h2>
          </ScrollReveal>
        </div>
        
        {/* Moving logos banner */}
        <div className="relative overflow-hidden">
          <div className="marquee-track">
            {[...PARTNERS, ...PARTNERS, ...PARTNERS].map((p, idx) => (
              <div key={`${p.name}-${idx}`} className="flex items-center px-8 md:px-12 shrink-0" data-testid={`partner-${idx}`}>
                <img
                  src={p.logo}
                  alt={p.name}
                  className="h-8 md:h-10 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                  style={{ 
                    maxWidth: '140px',
                    filter: (p.name === 'Markscon' || p.name === 'TM Strata') ? 'invert(1)' : 'none'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-8">
                <p className="text-neutral-500 text-xs font-semibold tracking-[0.25em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Work With Us
                </p>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 leading-[1.1]" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                  Let&apos;s discuss your cleaning requirements.
                </h2>
                <p className="text-neutral-600 text-lg mt-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Request a tailored quote for your site.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end md:items-center">
                <Link
                  to="/contact"
                  data-testid="cta-book-consult-btn"
                  className="inline-flex items-center gap-3 bg-[#B8D86B] text-neutral-900 px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#A3C65A] transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Request a Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Case Study Modal */}
      <AnimatePresence>
        {selectedProject && (
          <CaseStudyModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
            onNextProject={handleNextProject}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
